import { ApprovalStatus, AllApprovalStatusSubStates } from '../types';

// HWM-22633: derive the medical-check stage on the FE from the lead's
// `approval_status` + `approval_sub_status`. Mirrors the BE rules that
// used to live in `enterprise.lead_status_meta.compute_medical_check_status`
// (rtm-api). The dedicated BE column was deleted — FE owns the derivation.

export type MedicalCheckStatus = 'not_scheduled' | 'scheduled' | 'missed' | 'completed';

// SRS V4: QUALIFIED was retired and folded into PENDING_ENROLLMENT.
const PAST_MEDICAL_CHECK_STATUSES = new Set<string>(['PENDING_ENROLLMENT', 'APPROVED']);

// Accept both list-row shape (`approval_status` / `approval_sub_status`) and
// detail-DTO shape (`status` / `sub_status`). Backend wire formats differ —
// `PatientLead.get_dict` (list rows) emits `ApprovalStatus.name` UPPERCASE,
// while `get_lead_details` (detail DTO) emits `.name.lower()`. Normalise
// both reads to UPPERCASE here so this util's callers don't have to know
// which surface they're on.
export type MedicalCheckSource = {
    approval_status?: ApprovalStatus | string | null;
    approval_sub_status?: AllApprovalStatusSubStates | string | null;
    status?: ApprovalStatus | string | null;
    sub_status?: AllApprovalStatusSubStates | string | null;
};

const readStatus = (lead?: MedicalCheckSource | null): string =>
    (lead?.approval_status ?? lead?.status ?? '').toString().toUpperCase();

const readSubStatus = (lead?: MedicalCheckSource | null): string =>
    (lead?.approval_sub_status ?? lead?.sub_status ?? '').toString().toUpperCase();

export const getMedicalCheckStatus = (lead?: MedicalCheckSource | null): MedicalCheckStatus => {
    const status = readStatus(lead);
    if (PAST_MEDICAL_CHECK_STATUSES.has(status)) return 'completed';
    if (status === 'MEDICAL_CHECK') {
        const sub = readSubStatus(lead);
        if (sub === 'MISSED_APPOINTMENT') return 'missed';
        if (sub === 'IMPENDING_APPOINTMENT') return 'scheduled';
    }
    return 'not_scheduled';
};

// `LeadDetailsResponse` carries the scheduled timestamp on
// `status_change_note_date_time` — both DrChrono booking and the manual
// NewActivityForm flow stamp the slot there when the lead enters
// MEDICAL_CHECK. Return it only while the lead is still in MEDICAL_CHECK
// and not MISSED — once status moves on, the field is stale (the activity-
// log row retains it for history, but the action-card subtitle shouldn't).
export const getMedicalCheckScheduledAt = (
    lead?: (MedicalCheckSource & { status_change_note_date_time?: string | null }) | null
): string | null => {
    if (!lead) return null;
    if (readStatus(lead) !== 'MEDICAL_CHECK') return null;
    if (readSubStatus(lead) === 'MISSED_APPOINTMENT') return null;
    return lead.status_change_note_date_time ?? null;
};
